import { Actions } from '@client/new/helpers/types';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { useEffect, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import { createUseVM } from './form.utils';
import { toHIITExercise, toSet, toTabataExercise } from './workout.mappers';
import { hiitExerciseSchema, tabataExerciseSchema } from './workout.schema';
import { WorkoutVM } from './workout.viewModel';
import { sprinkles } from '@design-system/sprinkles.css';
import { HIITExercise, TabataExercise } from './workouts.domain';
import { Rep } from '../model/workout.domain';

type SubExerciseVMActionMap = {
  AddSet: null;
  RemoveSet: { setIndex: number };
  SetAMRAP: boolean;
  AddMedia: null;
  Remove: null;
};

export type SubExerciseVMAction = Actions<SubExerciseVMActionMap>;

export type SubExerciseVM = ReturnType<typeof useSubExerciseVM>;

export const useSubExerciseVM = (
  workoutVM: WorkoutVM,
  subExerciseId: string,
) => {
  const [isAMRAP, setIsAMRAP] = useState(false);

  const { type } = workoutVM.exerciseMap[subExerciseId];

  const vm = createUseVM(
    type === 'HIITExercise' ? hiitExerciseSchema : tabataExerciseSchema,
    (v) =>
      type === 'HIITExercise'
        ? toHIITExercise(v as HIITExercise['value'])
        : toTabataExercise(v as TabataExercise['value']),
  )(workoutVM, subExerciseId);

  const { append, remove, fields } = useFieldArray({
    control: vm.control,
    name: 'sets',
  });

  const sets = vm.watch('sets');
  const fieldsWithSets = fields.map((field, index) => ({
    ...field,
    ...sets[index],
  }));

  useEffect(() => {
    const amrapDisabled = Object.values(fieldsWithSets).some(
      (set) => set.rep.type !== 'AMRAP',
    );
    setIsAMRAP(!amrapDisabled);
  }, [fieldsWithSets]);

  const handleAMRAPChange = (value: boolean) => {
    setIsAMRAP(value);
    if (!value) return;
    vm.sets.forEach((_, i) => {
      vm.setValue(`sets.${i}.rep.type`, 'AMRAP');
      vm.setValue(`sets.${i}.rep.value`, 0);
    });
  };
  const settings = workoutVM.getSettings(subExerciseId);

  const dispatch = (a: SubExerciseVMAction) => {
    switch (a.type) {
      case 'AddSet': {
        append(
          toSet({
            time: settings.time,
            rest: settings.rest,
            rep: fieldsWithSets[0].rep as Rep,
          }),
        );
        vm.tick();
        break;
      }
      case 'RemoveSet':
        remove(a.payload.setIndex);
        vm.tick();

        break;
      case 'SetAMRAP':
        handleAMRAPChange(a.payload);
        break;
      case 'AddMedia':
        workoutVM.dispatch('openLibrary', {
          action: 'update',
          parentId: settings.id,
          exerciseId: subExerciseId,
        });
        break;
      case 'Remove':
        workoutVM.dispatch('removeSubExercise', { exerciseId: subExerciseId });
        break;
    }
  };

  const dispatchers = getDispatchers<SubExerciseVMActionMap>(dispatch);

  const menuItems = [
    {
      label: 'Remove',
      onClick: dispatchers.lazyDispatch('Remove'),
      className: sprinkles({ color: 'ERROR-100' }),
    },
  ];

  return {
    isAMRAP,
    menuItems,
    ...vm,
    ...dispatchers,
    fieldsWithSets,
  };
};
